import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useColorMode } from '@chakra-ui/react';
import { t } from 'i18next';

import { EVENT_GOODS_ITEM_TYPES, filterGoodsByCategory } from '../../../utils/EventGoodsHelper';
import { useQueryParams } from '../../../utils/useQueryParams';

import StickyHeader from '../../../components/StickyHeader';
import { useEventGoodsQuery } from '../../../components/api/queries/useEventGoodsQuery';
import CardGoods from '../../../presentation/components/card/CardGoods';
import { ReportFootprintBreadcrumb } from '../../../components/report/ReportFootprintBreadcrumb';
import StepComponent from '../../../components/steps/StepComponent';

import { Button } from '@peacecover/component';

export default function ReportEventGoodsPage() {
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [currentStep] = useState(0);

  // API에서 굿즈 리스트 가져오기
  const { data: eventGoods } = useEventGoodsQuery(selectedCategory);

  const navigate = useNavigate();
  const { queryParams } = useQueryParams();
  const { colorMode } = useColorMode();
  
  const navigateToNext = async () => {
    navigate(`/report-location/events/goods-type?${queryParams}`, {
      state: { selectedItems }
    });
  };

  const handleSelectItems = (id: string) => {
    setSelectedItems((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }
      return [...prev, id];
    });
  };

  const isSelectedItems = selectedItems.length > 0;

  return (
    <>
      <Helmet>
        <title>{t('ReportEventGoodsPage.pageName', { defaultValue: '제보하기' })}</title>
      </Helmet>
      <StickyHeader
        title={t('ReportEventGoodsPage.pageName', { defaultValue: '제보하기' }) as string}
        alwaysShowTitle={true}
        showBackButton={true}
        showTitle={true}
        onClickBackButton={() => {
          navigate('/collections?boxTabIndex=1');
        }}
      />
      <div className="flex flex-col pt-[calc(54px+env(safe-area-inset-top))] pb-[calc(128px+env(safe-area-inset-bottom))]">
        <div className="px-5 py-[10px]">
          <StepComponent currentStep={currentStep} nextStep={1} maxStep={5} title="">
            <div className="px-1 flex flex-col pb-[10px] gap-[18px]">
              <div className="mx-[-24px]">
                <ReportFootprintBreadcrumb />
              </div>
              <div className="flex flex-col gap-[14px]">
                <div className="text-[26px] font-bold whitespace-pre-wrap leading-tight text-text-main-lv1">
                  {t('ReportEventGoodsPage.title', {
                    defaultValue: '제공되는 모든 굿즈를\n선택해주세요!'
                  })}
                </div>
                <div className="text-[14px] text-text-main-lv4">
                  {t('ReportEventGoodsPage.description', {
                    defaultValue: '기본으로 제공되는 굿즈도 포함해주세요!'
                  })}
                </div>
              </div>
            </div>
            <div className="flex flex-nowrap mt-[16px] gap-[6px]">
              {EVENT_GOODS_ITEM_TYPES.map((item) => (
                <button
                  id={`menuButton_${item.id}`}
                  key={item.id}
                  type={'button'}
                  className={`px-[12px] py-[6px] gap-[4px] text-[13px] font-medium rounded-full ${
                    item.id === selectedCategory
                      ? 'bg-primary-light dark:bg-primary-dark text-on-primary'
                      : 'bg-secondary-light dark:bg-secondary-dark text-on-secondary'
                  }`}
                  onClick={() => setSelectedCategory(item.id)}
                >
                  {item.name}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3 w-full py-4">
              {filterGoodsByCategory(eventGoods, selectedCategory).map((goods) => {
                const isSelected = selectedItems.includes(goods.key);

                return (
                  <CardGoods
                    key={goods.key}
                    goodsKey={goods.key}
                    goodsName={goods.name}
                    isSelected={isSelected}
                    isShowCheckIcon={false}
                    onClick={() => handleSelectItems(goods.key)}
                  />
                );
              })}
            </div>
          </StepComponent>
        </div>
        <div
          className={`${
            colorMode === 'dark' ? 'darkBottomNavbar' : 'bottomNavbar'
          } bg-background-light dark:bg-background-dark text-center z-[9999]`}
          style={{
            padding: '10px 24px calc(10px + env(safe-area-inset-bottom)) 24px'
          }}
        >
          <Button
            size="large"
            btnColor="primary"
            disabled={!isSelectedItems}
            className="w-full rounded-[100px] text-[17px] font-semibold"
            onClick={navigateToNext}
          >
            {t('confirm')}
          </Button>
        </div>
      </div>
    </>
  );
}
